import { User } from '@/types';

/**
 * Local Storage Utility
 * Provides type-safe access to localStorage with error handling
 */
class StorageService {
  private prefix: string;

  constructor() {
    this.prefix = import.meta.env.VITE_STORAGE_PREFIX || 'cotaskr_';
  }

  /**
   * Get prefixed key
   */
  private getKey(key: string): string {
    return `${this.prefix}${key}`;
  }

  /**
   * Get item from storage
   */
  private getItem<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(this.getKey(key));
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(`Error reading ${key} from storage:`, error);
      return null;
    }
  }

  /**
   * Set item in storage
   */
  private setItem<T>(key: string, value: T): void {
    try {
      localStorage.setItem(this.getKey(key), JSON.stringify(value));
    } catch (error) {
      console.error(`Error writing ${key} to storage:`, error);
    }
  }

  /**
   * Remove item from storage
   */
  private removeItem(key: string): void {
    try {
      localStorage.removeItem(this.getKey(key));
    } catch (error) {
      console.error(`Error removing ${key} from storage:`, error);
    }
  }

  // ==================== Auth Methods ====================

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return this.getItem<string>('access_token');
  }

  /**
   * Set access token
   */
  setAccessToken(token: string): void {
    this.setItem('access_token', token);
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    return this.getItem<string>('refresh_token');
  }

  /**
   * Set refresh token
   */
  setRefreshToken(token: string): void {
    this.setItem('refresh_token', token);
  }

  /**
   * Get user data
   */
  getUser(): User | null {
    return this.getItem<User>('user');
  }

  /**
   * Set user data
   */
  setUser(user: User): void {
    this.setItem('user', user);
  }

  /**
   * Clear all auth data
   */
  clearAuth(): void {
    this.removeItem('access_token');
    this.removeItem('refresh_token');
    this.removeItem('user');
  }

  // ==================== Preference Methods ====================

  /**
   * Get theme preference
   */
  getTheme(): 'light' | 'dark' {
    return this.getItem<'light' | 'dark'>('theme') || 'light';
  }

  /**
   * Set theme preference
   */
  setTheme(theme: 'light' | 'dark'): void {
    this.setItem('theme', theme);
  }

  /**
   * Get sidebar collapsed state
   */
  getSidebarCollapsed(): boolean {
    return this.getItem<boolean>('sidebar_collapsed') || false;
  }

  /**
   * Set sidebar collapsed state
   */
  setSidebarCollapsed(collapsed: boolean): void {
    this.setItem('sidebar_collapsed', collapsed);
  }

  getSidebarWidth(): number | null {
    return this.getItem<number>('sidebar_width');
  }

  setSidebarWidth(width: number): void {
    this.setItem('sidebar_width', width);
  }

  // ==================== Recent Items ====================

  /**
   * Get recent items
   */
  getRecentItems<T>(key: string, maxItems = 5): T[] {
    const items = this.getItem<T[]>(`recent_${key}`) || [];
    return items.slice(0, maxItems);
  }

  /**
   * Add recent item
   */
  addRecentItem<T extends { id: string }>(key: string, item: T, maxItems = 5): void {
    const items = this.getRecentItems<T>(key, maxItems);
    const filtered = items.filter((i) => i.id !== item.id);
    const updated = [item, ...filtered].slice(0, maxItems);
    this.setItem(`recent_${key}`, updated);
  }

  // ==================== Generic Methods ====================

  /**
   * Get any item with type safety
   */
  get<T>(key: string): T | null {
    return this.getItem<T>(key);
  }

  /**
   * Set any item
   */
  set<T>(key: string, value: T): void {
    this.setItem(key, value);
  }

  /**
   * Remove any item
   */
  remove(key: string): void {
    this.removeItem(key);
  }

  /** Last calendar day (YYYY-MM-DD, user-local) the follow-up reminder was dismissed. */
  getFollowUpReminderDismissedDate(userId: string): string | null {
    return this.getItem<string>(`followup_reminder_dismissed_${userId}`);
  }

  setFollowUpReminderDismissedDate(userId: string, calendarDate: string): void {
    this.setItem(`followup_reminder_dismissed_${userId}`, calendarDate);
  }

  clearFollowUpReminderDismissed(userId: string): void {
    this.removeItem(`followup_reminder_dismissed_${userId}`);
  }

  /**
   * Clear all storage
   */
  clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }
}

// Export singleton instance
export const storage = new StorageService();
